import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIonRouter } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { useAppToast } from '@/hooks/useAppToast';
import type { MessageIntent } from '@/modules/template';
import {
  Destinations,
  ShareNavigationLogic,
  routesFor,
} from './navigation_logic';
import type {
  ShareDestination,
  ShareFlowShape,
  ShareNavDirection,
  ShareNavigator,
  ShareSurface,
} from './types';

/**
 * Leave nothing focused behind a navigation. An Ionic page that transitions
 * out while one of its inputs still holds focus keeps the mobile keyboard up
 * over the page that follows, and the focus ring lands on whatever the outlet
 * reuses. Shadow roots need walking by hand: `document.activeElement` stops at
 * the host of a web component.
 */
function blurActiveElement(): void {
  if (typeof document === 'undefined') return;

  const activeElement = document.activeElement;
  if (!(activeElement instanceof HTMLElement)) return;

  blurShadowActiveElement(activeElement);
  activeElement.blur();
}

function blurShadowActiveElement(element: HTMLElement): void {
  const activeElement = element.shadowRoot?.activeElement;
  if (!(activeElement instanceof HTMLElement)) return;

  blurShadowActiveElement(activeElement);
  activeElement.blur();
}

function routePath(value: string): string {
  try {
    return new URL(value, window.location.origin).pathname;
  } catch {
    return value.split('?')[0] ?? value;
  }
}

/**
 * The origin to return to, or nothing when it points back into the flow
 * itself — leaving sharing for a sharing URL would only re-enter it.
 */
function usableExitOrigin(
  origin: string | undefined,
  topicId: string
): string | undefined {
  const value = origin?.trim();
  if (!value || value === 'undefined') return undefined;

  const shareBase = `/topics/${encodeURIComponent(topicId)}/share`;
  return routePath(value).startsWith(shareBase) ? undefined : value;
}

function readRouterOrigin(from: string | undefined): string | undefined {
  return from && from !== 'undefined' && from.trim() !== '' ? from : undefined;
}

interface UseShareNavigatorOptions {
  topicId: string;
  /** Which surface is rendering this session. */
  surface: ShareSurface;
  /** What the session says about the shape of the flow. */
  flow: ShareFlowShape;
  /** Drops the persisted session — what leaving by the toolbar's X does. */
  discardSession: () => void;
}

/**
 * The share flow's step machine, as a navigator over named destinations.
 *
 * It owns the whole of where the sender is and how they move: the URL they are
 * on, the destinations this flow shows, how far along they are, and the three
 * ways out. The session state underneath it holds only what the sender has
 * chosen, and never a step or an index — which is what lets a second surface
 * drive the same session by mapping the same destinations onto its own screens.
 */
export function useShareNavigator({
  topicId,
  surface,
  flow,
  discardSession,
}: UseShareNavigatorOptions): ShareNavigator {
  const routes = routesFor(surface);
  const router = useIonRouter();
  const location = useLocation();
  const [presentToast] = useAppToast();

  const queryString = router.routeInfo?.search || location?.search || '';
  const routeOptions = router.routeInfo?.routeOptions as
    | { from?: string }
    | null
    | undefined;

  // Where the sender came from, kept so the flow can hand them back there. It
  // arrives either on the Ionic route options or as an ?origin= param, and
  // either can show up a render after the flow mounts, so both are latched
  // rather than read fresh each time.
  const [routerProvidedOrigin, setRouterProvidedOrigin] = useState<
    string | undefined
  >(() => readRouterOrigin(routeOptions?.from));
  const [urlProvidedOrigin, setUrlProvidedOrigin] = useState<
    string | undefined
  >(() => new URLSearchParams(queryString).get('origin') ?? undefined);

  useEffect(() => {
    if (!routerProvidedOrigin) {
      const from = readRouterOrigin(routeOptions?.from);
      if (from) setRouterProvidedOrigin(from);
    }
    if (!urlProvidedOrigin) {
      const origin = new URLSearchParams(queryString).get('origin');
      if (origin) setUrlProvidedOrigin(origin);
    }
  }, [queryString, routeOptions, routerProvidedOrigin, urlProvidedOrigin]);

  const urlExitOrigin = usableExitOrigin(urlProvidedOrigin, topicId);
  const routerExitOrigin = usableExitOrigin(routerProvidedOrigin, topicId);
  const topicPath = `/topics/${encodeURIComponent(topicId)}`;

  const at = routes.destinationFromPath(
    router.routeInfo?.pathname || location?.pathname || ''
  );

  const sequence = useMemo(() => routes.sequence(flow), [flow, routes]);

  const progress = ShareNavigationLogic.progressAt(
    sequence,
    at,
    flow.sendQueueCursor
  );

  const goTo = useCallback(
    (
      destination: ShareDestination,
      direction: ShareNavDirection = 'forward'
    ) => {
      blurActiveElement();
      // The origin rides along in the URL so a reload inside the flow still
      // knows where leaving it should land.
      const params = new URLSearchParams();
      if (urlExitOrigin) params.set('origin', urlExitOrigin);
      const path = routes.pathFor(topicId, destination, params.toString());
      router.push(path, direction, 'replace');
    },
    [router, routes, topicId, urlExitOrigin]
  );

  const goForward = useCallback(() => {
    goTo(
      ShareNavigationLogic.after(sequence, at) ??
        routes.nearest(Destinations.style),
      'forward'
    );
  }, [at, goTo, routes, sequence]);

  const goBack = useCallback(() => {
    goTo(
      ShareNavigationLogic.before(sequence, at) ?? Destinations.recipients,
      'back'
    );
  }, [at, goTo, sequence]);

  const goToFirstCompose = useCallback(() => {
    const destination = ShareNavigationLogic.firstCompose(
      flow.contactsByIntent
    );
    if (!destination) {
      presentToast({
        message: 'Add at least one recipient to continue',
        duration: 3000,
        color: 'warning',
      });
      return;
    }
    goTo(destination, 'forward');
  }, [flow.contactsByIntent, goTo, presentToast]);

  const nextCompose = useCallback(
    (intent: MessageIntent) =>
      ShareNavigationLogic.nextCompose(flow.contactsByIntent, intent),
    [flow.contactsByIntent]
  );

  const isLastMessage = useCallback(
    (destination: ShareDestination) =>
      ShareNavigationLogic.isLastMessage(sequence, destination),
    [sequence]
  );

  const exit = useCallback(() => {
    blurActiveElement();
    // Walk pushedByRoute so we land on the topic page rather than walking
    // back through the flow's own destinations (all of which use 'replace').
    router.goBack();
  }, [router]);

  const exitToOrigin = useCallback(() => {
    blurActiveElement();
    router.push(
      urlExitOrigin || routerExitOrigin || topicPath,
      'back',
      'replace'
    );
  }, [router, routerExitOrigin, topicPath, urlExitOrigin]);

  const exitAndDiscard = useCallback(() => {
    // The toolbar "X" leaves from any destination (issue #295), unlike the back
    // arrow's "interrupt", which drops the sender back on the recipients
    // destination. Drop the persisted session so re-entering starts fresh.
    discardSession();
    // Prefer popping the Ionic page stack so the flow's page actually unmounts.
    // A bare push to the topic route only swaps the URL while the share outlet
    // stays mounted on top — leaving from the recipient destination would keep
    // the recipient list on screen. Fall back to the explicit push only when
    // there is no history to pop: a cold load that landed straight in the flow
    // via an Android notification or a leg link.
    if (router.canGoBack()) {
      exit();
    } else {
      exitToOrigin();
    }
  }, [discardSession, exit, exitToOrigin, router]);

  // Held steady across renders: the navigator sits in the dependency arrays of
  // the effects that watch for a destination the session can no longer support
  // (the snap back to the recipients, the implied confirm), and a fresh object
  // every render would re-run them every render — including the one that
  // navigates, which would then navigate again.
  return useMemo(
    () => ({
      at,
      sequence,
      progress,
      goTo,
      goForward,
      goBack,
      goToFirstCompose,
      nextCompose,
      isLastMessage,
      exit,
      exitToOrigin,
      exitAndDiscard,
    }),
    [
      at,
      exit,
      exitAndDiscard,
      exitToOrigin,
      goBack,
      goForward,
      goTo,
      goToFirstCompose,
      isLastMessage,
      nextCompose,
      progress,
      sequence,
    ]
  );
}
